import React, { useState, useEffect, useMemo } from "react";
import { Settings, AlertTriangle } from "lucide-react";
import { Button } from "../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

import { useActiveDeal } from "../lib/deals/useActiveDeal";
import {
  createDefaultAssumptions,
  calculateRequiredStaffing,
} from "../lib/staffing/model";
import { getHardRuleFlags } from "../lib/staffing/flags";
import {
  coversPerDayFromUnderwriting,
  spaTreatmentsFromUnderwriting,
} from "../lib/underwriting/toStaffing";

import DealGuard, { DealGuardStatus } from "../components/common/DealGuard";
import CoverageHeatmap from "../components/staffing/CoverageHeatmap";
import RoleGapTable from "../components/staffing/RoleGapTable";
import AssumptionsDrawer from "../components/staffing/AssumptionsDrawer";

import { getTotalRooms } from "../lib/rooms";
import { Deal } from "@/types/deal";
import { patchProjectKpis } from "@/lib/datasource";
import { StaffingAssumptions, StaffingOverrides } from "@/types/staffing";
import {
  mapPayrollToRoles,
  NormalizedPayrollRole,
} from "@/lib/staffing/fromPayroll";
import { getPayrollDataById } from "@/hooks/payrollDataService";

export default function StaffingSenseCheckPage() {
  const { dealId, deal, status } = useActiveDeal();

  const [selectedYear, setSelectedYear] = useState<number>(3);
  const [assumptions, setAssumptions] = useState<StaffingAssumptions>(
    createDefaultAssumptions(),
  );
  const [overrides, setOverrides] = useState<StaffingOverrides>({});
  const [showAssumptions, setShowAssumptions] = useState<boolean>(false);
  const [payrollRoles, setPayrollRoles] = useState<NormalizedPayrollRole[]>([]);
  const [payrollLoading, setPayrollLoading] = useState<boolean>(false);

  useEffect(() => {
    if (deal?.kpis?.assumptions?.staffingAssumptions) {
      setAssumptions(
        deal.kpis.assumptions.staffingAssumptions as StaffingAssumptions,
      );
    } else {
      setAssumptions(createDefaultAssumptions());
    }

    if (deal?.kpis?.assumptions?.staffingOverrides) {
      setOverrides(
        deal.kpis.assumptions.staffingOverrides as StaffingOverrides,
      );
    } else {
      setOverrides({});
    }
  }, [deal]);

  useEffect(() => {
    const dataId = deal?.kpis?.payroll?.dataId;
    if (!dataId) {
      setPayrollRoles([]);
      return;
    }

    setPayrollLoading(true);
    getPayrollDataById(dataId)
      .then((record) => {
        if (record) {
          setPayrollRoles(mapPayrollToRoles(record));
        } else {
          setPayrollRoles([]);
        }
      })
      .catch((err) => {
        console.error("Failed to fetch payroll data:", err);
        setPayrollRoles([]);
      })
      .finally(() => setPayrollLoading(false));
  }, [deal?.kpis?.payroll?.dataId]);

  const linkedValues: any | undefined = useMemo(() => {
    if (!deal?.kpis?.roomRevenue || !deal?.kpis?.fnb) return undefined;

    try {
      const covers = coversPerDayFromUnderwriting(
        deal as Deal,
        selectedYear - 1,
      );
      const spa = spaTreatmentsFromUnderwriting(deal as Deal);
      const roomsSoldPerDay = deal.kpis.roomRevenue.totals?.roomsSold / 365;
      const guestsPerRoom = deal.kpis.fnb.avgGuests;

      return {
        breakfast: covers.breakfast,
        lunch: covers.lunch,
        dinner: covers.dinner,
        bar: covers.bar,
        spa: {
          treatmentsPerDay: spa.treatmentsPerDay,
          openHours: spa.openHours,
        },
        guestsPerRoom,
        roomsSoldPerDay,
      };
    } catch (e) {
      console.error("Failed to calculate linked values", e);
      return undefined;
    }
  }, [deal, selectedYear]);

  if (status !== "ready" || !deal) {
    return <DealGuard status={status as DealGuardStatus} />;
  }

  const rooms = getTotalRooms(deal);

  if (rooms === 0) {
    return (
      <div className="w-full max-w-[95vw] mx-auto rounded-xl border border-slate-200 bg-white p-6">
        <h1 className="text-xl font-semibold text-slate-900 mb-2">
          Staffing Sense Check
        </h1>
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
          <div className="flex items-center space-x-2">
            <AlertTriangle className="h-5 w-5 text-amber-600" />
            <p className="text-amber-800 font-medium">
              Add rooms in Property Details to analyze staffing requirements.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const exitSettings = deal.kpis?.exit;
  const exitYear = exitSettings?.holdPeriodYears ?? 10;
  const isPostExit = selectedYear > exitYear;

  const staffingData = calculateRequiredStaffing(
    deal as Deal,
    selectedYear,
    assumptions,
    overrides,
    payrollRoles,
  );

  const hardRuleFlags = getHardRuleFlags(staffingData);

  const handleSaveAssumptions = async (
    newAssumptions: StaffingAssumptions,
    newOverrides: StaffingOverrides,
  ) => {
    setAssumptions(newAssumptions);
    setOverrides(newOverrides);

    const kpisPatch = {
      assumptions: {
        ...deal.kpis.assumptions,
        staffingAssumptions: newAssumptions,
        staffingOverrides: newOverrides,
      },
    };

    try {
      await patchProjectKpis(dealId!, kpisPatch);
    } catch (error) {
      console.error("Failed to save assumptions, rolling back...", error);
      setAssumptions(
        deal?.kpis?.assumptions?.staffingAssumptions ||
          createDefaultAssumptions(),
      );
      setOverrides(deal?.kpis?.assumptions?.staffingOverrides || {});
      throw error;
    }
  };

  return (
    <div className="w-full max-w-[95vw] mx-auto space-y-6">
      <div className="w-full rounded-xl border border-slate-200 bg-white p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 mb-2">
              Staffing Sense Check
            </h1>
            <p className="text-sm text-slate-600">
              Compare current staffing vs operational requirements. Green =
              covered, red = shortfall.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-600">Analysis Year</span>
              <Select
                value={selectedYear.toString()}
                onValueChange={(v) => setSelectedYear(Number(v))}
              >
                <SelectTrigger className="h-9 w-24">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Array.from(
                    { length: Math.min(exitYear, 10) },
                    (_, i) => i + 1,
                  ).map((year) => (
                    <SelectItem key={year} value={year.toString()}>
                      Year {year}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Button
              variant="outline"
              onClick={() => setShowAssumptions(true)}
              className="flex items-center space-x-2"
            >
              <Settings className="h-4 w-4" />
              <span>Assumptions</span>
            </Button>
          </div>
        </div>
      </div>

      {isPostExit && (
        <div className="w-full rounded-lg border border-slate-200 bg-slate-50 p-4">
          <div className="flex items-center space-x-2">
            <AlertTriangle className="h-5 w-5 text-slate-600" />
            <span className="text-slate-700 font-medium text-sm">
              Exited in Year {exitYear} — staffing not required afterward
            </span>
          </div>
        </div>
      )}

      {payrollLoading && (
        <div className="text-sm text-slate-500 px-1">
          Загрузка данных payroll...
        </div>
      )}

      {!isPostExit && (
        <>
          <CoverageHeatmap staffingData={staffingData} />

          <RoleGapTable
            staffingData={staffingData}
            assumptions={assumptions}
            overrides={overrides}
            roomsSoldPerDay={
              deal.kpis?.roomRevenue
                ? deal.kpis.roomRevenue.totals?.roomsSold / 365
                : rooms * 0.7
            }
          />
        </>
      )}

      <AssumptionsDrawer
        isOpen={showAssumptions}
        onClose={() => setShowAssumptions(false)}
        dealId={dealId!}
        yearIdx={selectedYear - 1}
        assumptions={assumptions}
        onSave={handleSaveAssumptions}
        overrides={overrides}
        linkedValues={linkedValues}
      />

      <div className="w-full rounded-lg bg-slate-50 border border-slate-200 p-4">
        <div className="text-sm text-slate-600">
          <strong>What's this page?</strong> We check if your current team can
          realistically cover your opening hours and guest activity.
          Calculations use industry productivity benchmarks and account for
          holidays, sick days, and training time.
        </div>
      </div>
    </div>
  );
}
